import { useCallback, useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { columnFiltersToQueryParams, pageCount } from "@/core/api/pagination";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import { useQrCodesByMerchant } from "@/features/merchant/api/use-qr-codes-by-merchant";
import type { QrCodeDto } from "@/features/merchant/api/dto";
import { QrCodeFormDialog } from "@/features/merchant/components/qr-codes/qr-code-form-dialog";
import { QrCodePreviewDialog } from "@/features/merchant/components/qr-codes/qr-code-preview-dialog";
import { QrCodeRevokeDialog } from "@/features/merchant/components/qr-codes/qr-code-revoke-dialog";
import { QrCodeRowActions } from "@/features/merchant/components/qr-codes/qr-code-row-actions";
import {
  qrCodesColumnsFactory,
  type QrCodeRow,
} from "@/features/merchant/components/qr-codes/qr-codes-columns";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";

/** Section « QR codes » du détail marchand (liste + création + révocation). */
export function QrCodesSection({ merchantId }: { merchantId: string }) {
  const { t: tm } = useMerchantTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [previewTarget, setPreviewTarget] = useState<QrCodeDto | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<QrCodeRow | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      filters: columnFiltersToQueryParams(columnFilters),
    }),
    [pagination, columnFilters]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useQrCodesByMerchant(merchantId, params);

  const rows = useMemo(
    () => (data?.items ?? []).filter((qr): qr is QrCodeRow => Boolean(qr.id)),
    [data]
  );

  const handlePreview = useCallback((qr: QrCodeRow) => setPreviewTarget(qr), []);
  const handleRevoke = useCallback((qr: QrCodeRow) => setRevokeTarget(qr), []);

  const columns = useMemo(
    () =>
      qrCodesColumnsFactory((qr) => (
        <QrCodeRowActions
          qr={qr}
          onPreview={handlePreview}
          onRevoke={handleRevoke}
        />
      )),
    [handlePreview, handleRevoke]
  );

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">
          {tm("merchant.qrCodes.title")}
        </h2>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {tm("merchant.qrCodes.create")}
        </Button>
      </div>

      <DataTableClar
        data={rows}
        columnsClar={columns}
        title={tm("merchant.qrCodes.title")}
        exportFileName="qr-codes"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <QrCodeFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        merchantId={merchantId}
        onCreated={(created) => setPreviewTarget(created)}
      />
      <QrCodePreviewDialog
        qr={previewTarget}
        open={Boolean(previewTarget)}
        onOpenChange={(next) => (!next ? setPreviewTarget(null) : undefined)}
      />
      <QrCodeRevokeDialog
        qr={revokeTarget}
        open={Boolean(revokeTarget)}
        onOpenChange={(next) => (!next ? setRevokeTarget(null) : undefined)}
        merchantId={merchantId}
      />
    </section>
  );
}
